// src/components/layout/navSidebarSx.js
//
// Styling of the NAVIGATION sidebar in Layout.js, read from `theme.caro.sidebar` (spec
// 20260930-caro-editorial-theme, T045; AC-7, AC-8, LD-7, IR-4).
//
// Only a theme whose sidebar is `solid` (Caro Editorial: aubergine) gets styling here. For plum,
// gold and the presets these helpers add nothing and return today's values, so the glass sidebar
// renders exactly as before. Other Drawers (DetailDrawer, FilterDrawer, NotificationCenter,
// BottomSheet) never use these helpers and stay normal paper.

/** Attribute that marks the navigation drawer paper (for tests and targeted CSS). */
export const NAV_SIDEBAR_ATTR = 'data-caro-sidebar';

const solidSidebar = (theme) => (theme?.caro?.sidebar?.solid ? theme.caro.sidebar : null);

/** `PaperProps` for both navigation Drawers (temporary + permanent). */
export function navSidebarPaperProps(theme) {
  const sb = solidSidebar(theme);
  const props = { [NAV_SIDEBAR_ATTR]: 'true' };
  if (!sb) return props;
  props.sx = {
    bgcolor: sb.bg,
    color: sb.fg,
    // An opaque inverse surface: the glass blur of the base theme would only cost GPU time.
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    borderRight: 'none',
    '& .MuiDivider-root': { borderColor: sb.divider },
    '& .MuiListItemIcon-root': { color: 'inherit' },
    '& .MuiListItemButton-root:hover': { bgcolor: sb.itemHoverBg },
    '& .MuiListItemButton-root.Mui-selected, & .MuiListItemButton-root.Mui-selected:hover': {
      bgcolor: sb.itemSelectedBg,
      '& .MuiListItemText-primary': { fontWeight: 700 },
    },
  };
  return props;
}

/** Colour of the uppercase section headers ("MONITOR", "DIRECTORY", …). */
export const navSectionHeaderColor = (theme) => solidSidebar(theme)?.fgMuted ?? 'text.secondary';

/** Hover colour of the sidebar resize handle. */
export const navResizeHoverColor = (theme) => solidSidebar(theme)?.accent ?? 'primary.main';

/**
 * Logo on the navigation sidebar (owner decision D-6). On the aubergine Editorial sidebar the
 * plum/gold logo is barely visible, so it becomes a light silhouette of the SAME artwork: the
 * PNG has a transparent background, and `brightness(0) invert(1)` turns every opaque pixel white
 * (about 15:1 on #3E1729, far above the 3:1 needed for a non-text graphic). No second raster.
 * `public/logo/logo-full.svg` was considered but is a different, reduced drawing (no hand icon,
 * no "SMART"), so using it would change the brand mark rather than recolour it.
 */
export const navLogoStyle = (theme) =>
  solidSidebar(theme) ? { filter: 'brightness(0) invert(1)' } : {};
